import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { 
  PieChart, Pie, Cell, LineChart, Line, BarChart, Bar, 
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  Legend, FunnelChart, Funnel, LabelList 
} from 'recharts';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '@/common/SafeIcon';
import LiveActivity from './LiveActivity';

const { FiTrendingUp } = FiIcons;

const COLORS = ['#6366f1', '#14b8a6', '#f59e0b', '#8b5cf6', '#f43f5e'];
const ACCENT_TEAL = '#14b8a6';

const ChartWrapper = ({ title, children, badge, className }) => (
  <Card className={className}>
    <CardHeader className="pb-4 flex flex-row items-center justify-between border-b border-surface-50">
      <CardTitle className="text-[10px] font-black text-surface-400 uppercase tracking-[0.15em]">{title}</CardTitle>
      {badge}
    </CardHeader>
    <CardContent className="h-[300px] pt-8">
      {children}
    </CardContent>
  </Card>
);

const tooltipStyle = {
  contentStyle: { 
    borderRadius: '12px', 
    border: 'none', 
    boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', 
    fontSize: '11px',
    backgroundColor: '#fff' 
  },
  cursor: { fill: '#f8fafc' }
};

const ChartsSection = ({ giftDist, dailyTrend, cityStats, funnel, liveActivity }) => {
  const pieData = giftDist?.map(item => ({ name: item.gift, value: parseInt(item.total) })) || [];
  const lineData = dailyTrend?.map(item => ({ date: item.day, count: parseInt(item.total) })) || [];
  const cityData = cityStats?.slice(0, 5).map(item => ({ city: item.city, count: parseInt(item.total) })) || [];

  const funnelData = [
    { name: 'Uploaded', value: funnel?.uploaded || 0, fill: '#4f46e5' },
    { name: 'Gift Selected', value: funnel?.gift_selected || 0, fill: '#6366f1' },
    { name: 'Completed', value: funnel?.address_completed || 0, fill: '#818cf8' },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <ChartWrapper title="Gift Selection Distribution" className="xl:col-span-1">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={pieData} cx="50%" cy="50%" innerRadius={60} outerRadius={85} paddingAngle={5} dataKey="value">
                {pieData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} stroke="none" />)}
              </Pie>
              <Tooltip {...tooltipStyle} />
              <Legend iconType="circle" iconSize={8} wrapperStyle={{ fontSize: '10px', paddingTop: '10px' }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartWrapper>

        <ChartWrapper 
          title="Daily Conversion Trend" 
          className="xl:col-span-2"
          badge={<span className="text-[10px] font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded uppercase tracking-tighter">Activity Sync</span>}
        >
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={lineData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
              <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 10 }} />
              <YAxis axisLine={false} tickLine={false} tick={{ fill: '#94a3b8', fontSize: 10 }} />
              <Tooltip {...tooltipStyle} />
              <Line 
                type="monotone" 
                dataKey="count" 
                stroke="#6366f1" 
                strokeWidth={3} 
                dot={{ r: 3, fill: '#fff', strokeWidth: 2, stroke: '#6366f1' }} 
                activeDot={{ r: 6 }} 
              />
            </LineChart>
          </ResponsiveContainer>
        </ChartWrapper>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        <ChartWrapper 
          title="Top Performing Cities" 
          badge={<div className="text-[10px] font-bold text-teal-600 bg-teal-50 px-2 py-0.5 rounded uppercase font-black">Region Ranking</div>}
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={cityData} layout="vertical" margin={{ top: 5, right: 35, left: 10, bottom: 5 }}>
              <XAxis type="number" hide />
              <YAxis dataKey="city" type="category" axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 10 }} width={70} />
              <Tooltip {...tooltipStyle} />
              <Bar dataKey="count" fill={ACCENT_TEAL} radius={[0, 4, 4, 0]} barSize={18}>
                <LabelList dataKey="count" position="right" style={{ fill: '#64748b', fontSize: 10, fontWeight: 700 }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartWrapper>

        <ChartWrapper 
          title="Conversion Funnel" 
          badge={
            <div className="flex items-center text-[10px] font-black text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded uppercase">
              <SafeIcon icon={FiTrendingUp} className="w-3 h-3 mr-1" /> 
              {funnel?.conversion?.gift_selected_to_address_percent}% Success
            </div>
          }
        >
          <div className="flex flex-col h-full">
            <div className="flex-1 px-2 pt-2">
              <ResponsiveContainer width="100%" height="100%">
                <FunnelChart>
                  <Tooltip {...tooltipStyle} />
                  <Funnel 
                    dataKey="value" 
                    data={funnelData} 
                    isAnimationActive 
                    neckWidth="35%" 
                    neckHeight="25%"
                    labelLine={false}
                  >
                    <LabelList 
                      position="center" 
                      fill="#fff" 
                      stroke="none" 
                      dataKey="name" 
                      style={{ fontSize: 9, fontWeight: 800, textShadow: '0 2px 4px rgba(0,0,0,0.1)' }} 
                    />
                  </Funnel>
                </FunnelChart>
              </ResponsiveContainer>
            </div>
            
            <div className="grid grid-cols-2 gap-3 mt-8">
              <div className="p-3 bg-surface-50 rounded-2xl border border-surface-100 flex flex-col justify-center items-center text-center">
                <p className="text-[9px] uppercase font-black text-surface-400 tracking-[0.1em] mb-1">Gift Pick Rate</p>
                <span className="text-lg font-black text-indigo-600 leading-none">{funnel?.conversion?.uploaded_to_gift_selected_percent}%</span>
              </div>
              <div className="p-3 bg-indigo-600 rounded-2xl border border-indigo-500 shadow-lg shadow-indigo-100 flex flex-col justify-center items-center text-center">
                <p className="text-[9px] uppercase font-black text-indigo-100 tracking-[0.1em] mb-1">Final Verified</p>
                <span className="text-lg font-black text-white leading-none">{funnel?.conversion?.gift_selected_to_address_percent}%</span>
              </div>
            </div>
          </div>
        </ChartWrapper>

        <div className="h-[372px]">
          <LiveActivity activity={liveActivity} />
        </div>
      </div>
    </div>
  );
};

export default ChartsSection;